import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiDownload, apiRequest, ApiRequestError } from '../api/client.js';
import type {
  ProposalResponse,
  ProposalVersion,
  SubcontractorQuote,
  WorkOrderDetail,
} from '../api/types.js';
import { useSession } from '../auth/SessionProvider.js';
import { PERMISSIONS } from '../../shared/permissions.js';
import { COST_CATEGORY_LABELS, PROPOSAL_STATUS_LABELS } from '../../shared/workflow.js';
import type { CostCategory, ProposalStatus } from '../../shared/workflow.js';
import {
  Alert,
  Button,
  Card,
  EmptyState,
  Field,
  InternalPanel,
  Spinner,
  TextArea,
  classes,
} from '../components/ui.js';
import { formatBytes, formatCurrency, formatDateTime } from '../lib/format.js';
import { ProposalEditor } from './ProposalEditor.js';
import { ProposalDecisionForm } from './ProposalDecisionForm.js';

export function ProposalSection({ workOrder }: { workOrder: WorkOrderDetail }) {
  const queryClient = useQueryClient();
  const { can, isContractor, isCustomer, user } = useSession();
  const [editingVersionId, setEditingVersionId] = useState<string | null>(null);
  const [creating, setCreating] = useState<
    false | 'estimate' | 'final_invoice' | 'proceed_authorization'
  >(false);
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['proposals', workOrder.id],
    queryFn: () => apiRequest<ProposalResponse>(`/api/work-orders/${workOrder.id}/proposals`),
  });

  const submit = useMutation({
    mutationFn: (versionId: string) =>
      apiRequest(`/api/proposal-versions/${versionId}/submit`, { method: 'POST' }),
    onSuccess: () => invalidate(),
    onError: (caught: unknown) => {
      setError(
        caught instanceof ApiRequestError
          ? caught.message
          : 'The proposal could not be submitted. Please try again.',
      );
    },
  });

  const withdraw = useMutation({
    mutationFn: (versionId: string) =>
      apiRequest(`/api/proposal-versions/${versionId}/withdraw`, { method: 'POST' }),
    onSuccess: () => invalidate(),
    onError: (caught: unknown) => {
      setError(
        caught instanceof ApiRequestError
          ? caught.message
          : 'The proposal could not be withdrawn. Please try again.',
      );
    },
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['proposals', workOrder.id] });
    void queryClient.invalidateQueries({ queryKey: ['work-order', workOrder.id] });
    void queryClient.invalidateQueries({ queryKey: ['timeline', workOrder.id] });
    void queryClient.invalidateQueries({ queryKey: ['work-orders'] });
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  };

  if (isLoading) return <Spinner label="Loading proposal" />;

  const versions = data?.versions ?? [];
  const current = versions[0];
  const mayManage =
    isContractor &&
    can(PERMISSIONS.PROPOSAL_MANAGE) &&
    !['closed', 'cancelled'].includes(workOrder.status);
  const maySubmit = isContractor && can(PERMISSIONS.PROPOSAL_SUBMIT);
  const mayDecide = isCustomer && can(PERMISSIONS.PROPOSAL_APPROVE);
  const hasDraft = versions.some((version) => version.status === 'draft');
  // USPS approves a quoted work order once. Work authorized to proceed against a
  // not-to-exceed amount is priced again from the actual invoice before closure.
  const invoiceApproved = versions.some(
    (version) => version.kind === 'final_invoice' && version.status === 'approved',
  );
  const invoicePending = current?.kind === 'final_invoice' && current.status === 'submitted';
  const authorizedToProceed = versions.some(
    (version) => version.kind === 'proceed_authorization' && version.status === 'approved',
  );
  const invoiceRequired = authorizedToProceed;
  const urgentWork =
    workOrder.emergencyCallout || ['emergency', 'urgent'].includes(workOrder.priority);
  const mayStartFinalInvoice =
    isContractor &&
    authorizedToProceed &&
    can(PERMISSIONS.PROPOSAL_MANAGE) &&
    ['approved', 'procurement', 'repair_scheduled', 'work_in_progress', 'completed'].includes(
      workOrder.status,
    ) &&
    !hasDraft &&
    !invoiceApproved;
  // Approval authority under the agreement is bounded by dollar value, so holding the
  // permission is not enough; the server states whether this reader's authority covers
  // the amount, and refuses the decision again if the form is bypassed.
  const withinAuthority = current?.viewerMayApprove ?? false;
  const mayEndorse = current?.viewerMayEndorse ?? false;
  const mayReferToCo =
    mayDecide &&
    current?.status === 'submitted' &&
    withinAuthority &&
    !(user?.roleCodes ?? []).includes('customer_co');

  if (creating || editingVersionId) {
    const editing = versions.find((version) => version.id === editingVersionId);
    return (
      <ProposalEditor
        workOrderId={workOrder.id}
        supplies={workOrder.kind === 'supplies'}
        version={editing}
        kind={creating || 'estimate'}
        onDone={() => {
          setCreating(false);
          setEditingVersionId(null);
          invalidate();
        }}
        onSubmitRequested={(versionId) => {
          setCreating(false);
          setEditingVersionId(null);
          submit.mutate(versionId);
        }}
        onCancel={() => {
          setCreating(false);
          setEditingVersionId(null);
        }}
      />
    );
  }

  return (
    <div className="flex flex-col gap-5">
      {error && <Alert variant="error">{error}</Alert>}

      {mayDecide && current?.status === 'submitted' && (withinAuthority || mayEndorse) && (
        <ProposalDecisionForm
          workOrderNumber={workOrder.workOrderNumber}
          version={current}
          onDecided={invalidate}
          endorse={!withinAuthority && mayEndorse}
        />
      )}

      {mayDecide &&
        current?.status === 'submitted' &&
        !withinAuthority &&
        !mayEndorse &&
        (current.awaitingCorReview ? (
          <Alert variant="info" title="COR review required first">
            This proposal totals {formatCurrency(current.totalCents)}, above the COR limit. The COR
            must review and endorse it before it comes to the Contracting Officer for final
            approval.
          </Alert>
        ) : (
          <Alert
            variant="info"
            title={`${current.requiredAuthorityLabel ?? 'Higher'} approval required`}
          >
            This proposal totals {formatCurrency(current.totalCents)}, which is above your approval
            limit under the agreement. It is waiting on the{' '}
            {current.requiredAuthorityLabel ?? 'authority that may approve this amount'}.
          </Alert>
        ))}

      {mayReferToCo && current && <ReferToCoCard versionId={current.id} onReferred={invalidate} />}

      {(invoiceRequired || invoiceApproved || invoicePending || mayStartFinalInvoice) &&
        versions.length > 0 && (
          <Alert
            variant={
              invoiceApproved ? 'success' : invoiceRequired || invoicePending ? 'warning' : 'info'
            }
            title={
              invoiceApproved
                ? 'Final invoice approved'
                : invoicePending
                  ? 'Awaiting invoice approval'
                  : 'Authorized to proceed: final invoice approval required before closure'
            }
          >
            {invoiceApproved
              ? 'USPS has approved the final invoice; the work order may be closed.'
              : invoicePending
                ? 'The final invoice has been submitted to USPS for its second approval.'
                : isContractor
                  ? 'USPS authorized this work before it was priced. Dispatch the subcontractor; when the invoice arrives, submit the final invoice here for USPS approval of the actual amount before the work order can be closed.'
                  : 'You authorized VGS to proceed before a price was known. When VGS submits the final invoice it will come back to you for approval of the actual amount.'}
            {mayStartFinalInvoice && (
              <div className="mt-3">
                <Button onClick={() => setCreating('final_invoice')}>Submit final invoice</Button>
              </div>
            )}
          </Alert>
        )}

      {versions.length === 0 ? (
        <Card
          title="Proposal"
          actions={
            mayManage && (
              <span className="flex flex-wrap gap-2">
                {urgentWork && (
                  <Button onClick={() => setCreating('proceed_authorization')}>
                    Request authorization to proceed
                  </Button>
                )}
                <Button
                  variant={urgentWork ? 'secondary' : 'primary'}
                  onClick={() => setCreating('estimate')}
                >
                  Prepare proposal
                </Button>
              </span>
            )
          }
        >
          <EmptyState
            title="No proposal yet"
            description={
              isContractor
                ? urgentWork
                  ? 'Emergency or urgent work with no quote yet: request authorization to proceed so USPS can approve dispatch now. If you already have a price, prepare a proposal instead.'
                  : 'Prepare a proposal with the cost items for this repair.'
                : 'VGS will submit a proposal for your approval once the repair has been scoped.'
            }
          />
        </Card>
      ) : (
        versions.map((version) => (
          <Card
            key={version.id}
            title={
              <span className="flex flex-wrap items-center gap-2">
                <span className="text-base font-semibold text-slate-900">
                  Version {version.versionNo}
                </span>
                <ProposalStatusBadge status={version.status as ProposalStatus} />
                {version.kind === 'proceed_authorization' && (
                  <span className="rounded bg-orange-100 px-1.5 py-0.5 text-xs font-semibold text-orange-800">
                    Authorization to proceed{version.totalCents > 0 ? ' · NTE' : ' · no estimate'}
                  </span>
                )}
                {version.kind === 'final_invoice' && (
                  <span className="rounded bg-violet-100 px-1.5 py-0.5 text-xs font-semibold text-violet-800">
                    Final invoice{version.invoiceNumber ? ` ${version.invoiceNumber}` : ''}
                  </span>
                )}
                {version.status === 'submitted' && version.requiredAuthorityLabel && (
                  <span className="rounded bg-sky-100 px-1.5 py-0.5 text-xs font-semibold text-sky-800">
                    {version.awaitingCorReview
                      ? 'COR review, then Contracting Officer approval'
                      : `${version.requiredAuthorityLabel} approval required`}
                  </span>
                )}
                {version.withdrawnAt && (
                  <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs font-semibold text-slate-600">
                    Withdrawn by VGS {new Date(version.withdrawnAt).toLocaleDateString()}
                  </span>
                )}
                {version.corEndorsedAt && (
                  <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-xs font-semibold text-emerald-800">
                    Endorsed by {version.corEndorsedByName ?? 'the COR'} (COR){' '}
                    {new Date(version.corEndorsedAt).toLocaleDateString()}
                  </span>
                )}
              </span>
            }
            actions={
              <>
                {mayManage && version.status === 'draft' && (
                  <Button variant="secondary" onClick={() => setEditingVersionId(version.id)}>
                    Edit draft
                  </Button>
                )}
                {maySubmit && version.status === 'draft' && (
                  <Button
                    onClick={() => {
                      setError(null);
                      submit.mutate(version.id);
                    }}
                    loading={submit.isPending}
                  >
                    Submit to USPS
                  </Button>
                )}
                {maySubmit && version.status === 'submitted' && (
                  <Button
                    variant="secondary"
                    onClick={() => {
                      if (
                        !window.confirm(
                          'Withdraw this proposal from USPS? It will be kept in the history and you can prepare a corrected version to submit.',
                        )
                      )
                        return;
                      setError(null);
                      withdraw.mutate(version.id);
                    }}
                    loading={withdraw.isPending}
                  >
                    Withdraw and correct
                  </Button>
                )}
                {mayManage &&
                  !hasDraft &&
                  ['revision_requested', 'rejected'].includes(version.status) &&
                  version === current && (
                    <Button
                      onClick={() =>
                        setCreating(version.kind === 'final_invoice' ? 'final_invoice' : 'estimate')
                      }
                    >
                      Prepare revision
                    </Button>
                  )}
                {mayManage &&
                  !hasDraft &&
                  version === current &&
                  version.status === 'superseded' &&
                  version.kind === 'estimate' &&
                  ['under_vgs_review', 'estimate_in_progress', 'revision_requested'].includes(
                    workOrder.status,
                  ) && (
                    <Button onClick={() => setCreating('estimate')}>
                      Prepare corrected proposal
                    </Button>
                  )}
              </>
            }
          >
            <div className="flex flex-col gap-4">
              {version.customerComment && (
                <Alert
                  variant={commentVariant(version.status)}
                  title={commentTitle(version.status)}
                >
                  {version.customerComment}
                </Alert>
              )}

              {version.scopeOfWork && (
                <section>
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Scope of work
                  </h3>
                  <p className="mt-1 whitespace-pre-wrap text-sm text-slate-800">
                    {version.scopeOfWork}
                  </p>
                </section>
              )}

              {version.findingsSummary && (
                <section>
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Findings summary
                  </h3>
                  <p className="mt-1 whitespace-pre-wrap text-sm text-slate-800">
                    {version.findingsSummary}
                  </p>
                </section>
              )}

              <CostTable version={version} />

              <QuoteList
                quotes={quotesForVersion(
                  data?.subcontractorQuotes ?? [],
                  version,
                  version === current,
                )}
                needsQuote={
                  version.costItems.some((item) => item.costCategory === 'subcontractor') &&
                  quotesForVersion(data?.subcontractorQuotes ?? [], version, version === current)
                    .length === 0
                }
                isContractor={isContractor}
              />

              {version.internalNotes !== undefined && (
                <InternalPanel title="VGS internal notes">
                  <p className="whitespace-pre-wrap text-sm text-slate-800">
                    {version.internalNotes ?? 'None recorded.'}
                  </p>
                  {version.preparedByName && (
                    <p className="mt-2 text-xs text-slate-600">
                      Prepared by {version.preparedByName}
                      {version.submittedByName ? ` · submitted by ${version.submittedByName}` : ''}
                    </p>
                  )}
                </InternalPanel>
              )}

              <p className="text-xs text-slate-600">
                {version.submittedAt
                  ? `Submitted ${formatDateTime(version.submittedAt)}`
                  : `Drafted ${formatDateTime(version.createdAt)}`}
                {version.customerResponseAt
                  ? ` · USPS responded ${formatDateTime(version.customerResponseAt)}`
                  : ''}
              </p>
            </div>
          </Card>
        ))
      )}

      {(data?.approvals.length ?? 0) > 0 && (
        <Card title="Approval record">
          <p className="mb-3 text-sm text-slate-600">
            These records are the artifact of USPS authorization and cannot be altered.
          </p>
          <ul className="divide-y divide-slate-200">
            {data?.approvals.map((approval) => (
              <li key={approval.id} className="flex flex-col gap-1 py-3 first:pt-0 last:pb-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={classes(
                      'rounded px-1.5 py-0.5 text-xs font-semibold',
                      approval.decision === 'approved'
                        ? 'bg-emerald-100 text-emerald-800'
                        : approval.decision === 'rejected'
                          ? 'bg-rose-100 text-rose-800'
                          : 'bg-amber-100 text-amber-800',
                    )}
                  >
                    {approval.decision === 'revision_requested'
                      ? 'Revision requested'
                      : approval.decision === 'approved'
                        ? 'Approved'
                        : 'Rejected'}
                  </span>
                  <span className="text-sm text-slate-800">Version {approval.versionNo}</span>
                  {approval.approvedAmountCents !== null && (
                    <span className="text-sm font-semibold tabular-nums text-slate-900">
                      {formatCurrency(approval.approvedAmountCents)}
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-600">
                  {approval.deciderName}
                  {approval.authorityLabel ? ` (${approval.authorityLabel})` : ''} ·{' '}
                  {formatDateTime(approval.decidedAt)}
                  {approval.fundingReference ? ` · funding ${approval.fundingReference}` : ''}
                  {approval.purchaseOrderNumber ? ` · PO ${approval.purchaseOrderNumber}` : ''}
                </p>
                {approval.authorityBasis && (
                  <p className="text-xs text-slate-500">Authority: {approval.authorityBasis}</p>
                )}
                {approval.comment && (
                  <p className="whitespace-pre-wrap text-sm text-slate-800">{approval.comment}</p>
                )}
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

/**
 * A COR may decide within their limit but can hand the proposal to the Contracting
 * Officer instead. The referral is recorded so the reason the CO decided is visible
 * on the work order rather than only in someone's inbox.
 */
function ReferToCoCard({ versionId, onReferred }: { versionId: string; onReferred: () => void }) {
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);

  const refer = useMutation({
    mutationFn: () =>
      apiRequest(`/api/proposal-versions/${versionId}/refer-to-co`, {
        method: 'POST',
        body: { comment: comment.trim() || undefined },
      }),
    onSuccess: () => {
      setComment('');
      onReferred();
    },
    onError: (caught: unknown) => {
      setError(
        caught instanceof ApiRequestError
          ? caught.message
          : 'The referral could not be recorded. Please try again.',
      );
    },
  });

  return (
    <Card title="Refer to the Contracting Officer">
      {error && <Alert variant="error">{error}</Alert>}
      <p className="text-sm text-slate-700">
        This proposal is within your approval authority. Refer it to the Contracting Officer if they
        should decide it instead.
      </p>
      <div className="mt-3 flex flex-col gap-2">
        <Field label="Reason for referral" htmlFor="referralComment">
          <TextArea
            id="referralComment"
            rows={2}
            value={comment}
            onChange={(event) => setComment(event.target.value)}
          />
        </Field>
        <div>
          <Button variant="secondary" loading={refer.isPending} onClick={() => refer.mutate()}>
            Refer to Contracting Officer
          </Button>
        </div>
      </div>
    </Card>
  );
}

/**
 * The USPS comment accompanies any decision, including an approval, so it is titled
 * by the decision it belongs to rather than assumed to be a revision request.
 */
function commentTitle(status: ProposalStatus): string {
  switch (status) {
    case 'rejected':
      return 'USPS rejected this version';
    case 'revision_requested':
      return 'USPS requested a revision';
    case 'approved':
      return 'USPS approved this version';
    default:
      return 'Note from USPS';
  }
}

function commentVariant(status: ProposalStatus): 'error' | 'warning' | 'success' | 'info' {
  switch (status) {
    case 'rejected':
      return 'error';
    case 'revision_requested':
      return 'warning';
    case 'approved':
      return 'success';
    default:
      return 'info';
  }
}

/**
 * A quote uploaded before the proposal version existed carries no version link, so
 * those are shown against the version currently under review.
 */
function quotesForVersion(
  quotes: readonly SubcontractorQuote[],
  version: ProposalVersion,
  isCurrent: boolean,
): SubcontractorQuote[] {
  return quotes.filter(
    (quote) =>
      quote.proposalVersionId === version.id || (quote.proposalVersionId === null && isCurrent),
  );
}

function QuoteList({
  quotes,
  needsQuote,
  isContractor,
}: {
  quotes: readonly SubcontractorQuote[];
  needsQuote: boolean;
  isContractor: boolean;
}) {
  if (quotes.length === 0 && !needsQuote) return null;

  return (
    <section>
      <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        Subcontractor quotes
      </h3>
      {quotes.length === 0 ? (
        <Alert variant="warning" className="mt-1">
          {isContractor
            ? 'This proposal includes subcontracted work. Attach the subcontractor quote as a USPS-visible document before submitting.'
            : 'No subcontractor quote has been provided for this proposal yet.'}
        </Alert>
      ) : (
        <ul className="mt-1 divide-y divide-slate-200">
          {quotes.map((quote) => (
            <li key={quote.id} className="py-2 first:pt-0 last:pb-0">
              <button
                type="button"
                onClick={() =>
                  void apiDownload(`/api/attachments/${quote.id}/content`, quote.fileName)
                }
                className="text-sm font-medium text-brand-800 hover:underline"
              >
                {quote.fileName}
              </button>
              <p className="text-xs text-slate-600">
                {formatBytes(quote.byteSize)} · uploaded {formatDateTime(quote.uploadedAt)}
              </p>
              {quote.description && <p className="text-sm text-slate-800">{quote.description}</p>}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function CostTable({ version }: { version: ProposalVersion }) {
  if (version.costItems.length === 0) {
    return <p className="text-sm text-slate-600">No cost items on this version.</p>;
  }

  const withheld = version.withheldSubcontractLineCount;

  return (
    <div className="overflow-x-auto">
      {withheld > 0 && (
        <p className="mb-3 text-sm text-slate-600">
          {withheld === 1 ? 'One subcontracted line is' : `${withheld} subcontracted lines are`} not
          itemized above; the supporting quote is held as VGS-internal. The total shown includes
          this work.
        </p>
      )}
      <table className="min-w-full divide-y divide-slate-200">
        <caption className="sr-only">Cost items for version {version.versionNo}</caption>
        <thead className="bg-slate-50">
          <tr>
            <th scope="col" className="table-header-cell">
              Category
            </th>
            <th scope="col" className="table-header-cell">
              Description
            </th>
            <th scope="col" className="table-header-cell text-right">
              Qty
            </th>
            <th scope="col" className="table-header-cell text-right">
              Unit price
            </th>
            <th scope="col" className="table-header-cell text-right">
              Amount
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {version.costItems.map((item) => (
            <tr key={item.id}>
              <td className="table-cell-base">
                {COST_CATEGORY_LABELS[item.costCategory as CostCategory] ?? item.costCategory}
              </td>
              <td className="table-cell-base">{item.description}</td>
              <td className="table-cell-base text-right tabular-nums">
                {item.quantity}
                {item.unit ? ` ${item.unit}` : ''}
              </td>
              <td className="table-cell-base text-right tabular-nums">
                {formatCurrency(item.unitPriceCents)}
              </td>
              <td className="table-cell-base text-right tabular-nums">
                {formatCurrency(item.extendedAmountCents)}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot className="border-t-2 border-slate-300">
          <tr>
            <th scope="row" colSpan={4} className="px-3 py-2 text-right text-sm text-slate-700">
              Subtotal
            </th>
            <td className="px-3 py-2 text-right text-sm tabular-nums text-slate-900">
              {formatCurrency(version.subtotalCents)}
            </td>
          </tr>
          <tr>
            <th scope="row" colSpan={4} className="px-3 py-2 text-right text-sm text-slate-700">
              Markup ({version.markupPercentDisplay})
            </th>
            <td className="px-3 py-2 text-right text-sm tabular-nums text-slate-900">
              {formatCurrency(version.markupCents)}
            </td>
          </tr>
          <tr>
            <th
              scope="row"
              colSpan={4}
              className="px-3 py-2 text-right text-sm font-semibold text-slate-900"
            >
              Total
            </th>
            <td className="px-3 py-2 text-right text-base font-semibold tabular-nums text-slate-900">
              {formatCurrency(version.totalCents)}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function ProposalStatusBadge({ status }: { status: ProposalStatus }) {
  const tone: Record<ProposalStatus, string> = {
    draft: 'bg-slate-100 text-slate-700',
    submitted: 'bg-amber-100 text-amber-800',
    approved: 'bg-emerald-100 text-emerald-800',
    revision_requested: 'bg-amber-100 text-amber-800',
    rejected: 'bg-rose-100 text-rose-800',
    superseded: 'bg-slate-100 text-slate-500',
  };
  return (
    <span className={classes('rounded px-1.5 py-0.5 text-xs font-semibold', tone[status])}>
      {PROPOSAL_STATUS_LABELS[status] ?? status}
    </span>
  );
}
